import { z } from 'zod';
import { PrintMethodSchema, RectMmSchema, ViewIdSchema } from './design.js';
export const PrintAreaSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    viewId: ViewIdSchema,
    rect: RectMmSchema,
    bleedMm: z.number().min(0).default(5),
    minDpi: z.number().positive().default(150),
});
export const TemplateViewSchema = z.object({
    id: ViewIdSchema,
    label: z.string().min(1),
    /**
     * Silhouette SVG/PNG key in R2.
     * Optional for shading-only products (mugs, flat photos that do not tint).
     * Empty or `…/_pending/…` means “not uploaded”.
     */
    silhouetteKey: z.string().default(''),
    /**
     * Shading PNG/WebP key in R2.
     * Optional when a raster silhouette alone is used as the mockup photo.
     */
    shadingKey: z.string().default(''),
    /** Mockup pixel width of the silhouette / shading asset */
    assetWidthPx: z.number().positive(),
    assetHeightPx: z.number().positive(),
    /**
     * Calibration: how many mockup pixels equal 1 mm of physical print.
     * Derived in admin as drawnRectPx / declaredPrintWidthMm.
     */
    pxPerMm: z.number().positive(),
    versionStamp: z.string().min(1),
    printAreas: z.array(PrintAreaSchema).min(1),
});
export const MockupModeSchema = z.enum(['tinted', 'photo']);
export const TemplateColorSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    hex: z.string().regex(/^#?[0-9a-fA-F]{6}$/),
    isDark: z.boolean(),
    mode: MockupModeSchema.default('tinted'),
    /** Optional per-view photo override keys when mode === 'photo' */
    photoKeys: z.record(ViewIdSchema, z.string()).optional(),
    /** Optional per-color print-area overrides keyed by printAreaId */
    printAreaOverrides: z.record(z.string(), RectMmSchema).optional(),
    available: z.boolean().default(true),
});
export const ShadingBlendSchema = z.enum(['multiply', 'over']);
export const ProductTemplateSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    printMethod: PrintMethodSchema,
    minDpi: z.number().positive().default(150),
    views: z.array(TemplateViewSchema).min(1),
    colors: z.array(TemplateColorSchema).min(1),
    defaultColorKey: z.string().min(1),
    defaultViewId: ViewIdSchema.default('front'),
    /**
     * How shading composites onto the tinted silhouette.
     * - `over` — FPD-style: transparent PNG shadows on top (whites stay white).
     * - `multiply` — darkens base by shading luminance (legacy mockup packs).
     */
    shadingBlend: ShadingBlendSchema.default('over'),
    /** null/omit = global catalog; set = private to that store */
    storeId: z.string().min(1).nullable().optional(),
});
export const RenderProfileSchema = z.object({
    method: PrintMethodSchema,
    dpi: z.number().positive().default(300),
    format: z.enum(['png', 'pdf', 'both']).default('both'),
    /** DTG on dark garments may need white underbase flag */
    underbase: z.boolean().default(false),
    transparent: z.boolean().default(true),
});
//# sourceMappingURL=template.js.map